import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { apiService } from '../services/api';

const TOKEN_KEY = 'uem_token';
const USER_KEY = 'uem_user';
const ACCESS_KEY = 'uem_access';
const PORTAL_TENANT_KEY = 'platform_active_tenant_id';
const PORTAL_TENANT_NAME_KEY = 'platform_active_tenant_name';

export interface AuthUser {
  id: string;
  username: string;
  full_name: string | null;
  email: string | null;
  role?: string | null;
  tenant_id?: string | null;
  tenant_code?: string | null;
  tenant_name?: string | null;
  is_platform_admin?: boolean;
  is_platform_staff?: boolean;
  platform_role?: string | null;
}

export interface AccessSnapshot {
  permissions: string[];
  enabled_modules: string[];
  enabled_pages: string[];
  enabled_os: { platform: string; enabled: boolean; allowed_modes?: string[] }[];
  nav_page_map?: Record<string, string>;
  branding?: Record<string, unknown> | null;
}

interface AuthContextType {
  token: string | null;
  user: AuthUser | null;
  access: AccessSnapshot | null;
  /** True when the entitlement snapshot could not be loaded from the server. */
  accessError: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  isPlatformStaff: boolean;
  isPlatformAdmin: boolean;
  portalTenantId: string | null;
  portalTenantName: string | null;
  enterClientPortal: (tenantId: string, tenantName?: string) => void;
  exitClientPortal: () => void;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  setToken: (token: string | null, user?: AuthUser | null) => void;
  refreshAccess: () => Promise<void>;
  can: (permissionKey: string) => boolean;
  canAny: (keys: string[]) => boolean;
  pageEnabled: (pageKey: string) => boolean;
  osEnabled: (platform: string) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const u = localStorage.getItem(USER_KEY);
      return u ? JSON.parse(u) : null;
    } catch {
      return null;
    }
  });
  const [access, setAccess] = useState<AccessSnapshot | null>(() => {
    try {
      const a = localStorage.getItem(ACCESS_KEY);
      return a ? JSON.parse(a) : null;
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [accessError, setAccessError] = useState(false);
  const [portalTenantId, setPortalTenantId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(PORTAL_TENANT_KEY);
    } catch {
      return null;
    }
  });
  const [portalTenantName, setPortalTenantName] = useState<string | null>(() => {
    try {
      return localStorage.getItem(PORTAL_TENANT_NAME_KEY);
    } catch {
      return null;
    }
  });

  const roleUpper = String(user?.role || '').toUpperCase();
  const isPlatformAdmin = !!(user?.is_platform_admin || roleUpper === 'PLATFORM_ADMIN');
  const isPlatformStaff = !!(
    user?.is_platform_staff ||
    isPlatformAdmin ||
    ['PLATFORM_SUPPORT', 'PLATFORM_BILLING', 'PLATFORM_VIEWER'].includes(roleUpper)
  );

  const clearGovernancePath = useCallback(() => {
    try {
      if (typeof window === 'undefined') return;
      const p = window.location.pathname || '/';
      if (
        p.startsWith('/policies/') ||
        p.startsWith('/compliance/') ||
        p.startsWith('/assets') ||
        p.startsWith('/software-metering')
      ) {
        window.history.replaceState({}, '', '/');
      }
    } catch {
      /* ignore */
    }
  }, []);

  const enterClientPortal = useCallback((tenantId: string, tenantName?: string) => {
    setPortalTenantId(tenantId);
    setPortalTenantName(tenantName || null);
    try {
      localStorage.setItem(PORTAL_TENANT_KEY, tenantId);
      if (tenantName) localStorage.setItem(PORTAL_TENANT_NAME_KEY, tenantName);
      else localStorage.removeItem(PORTAL_TENANT_NAME_KEY);
    } catch {
      /* ignore */
    }
    // Drop stale deep links so refresh does not reopen the wrong shell page.
    clearGovernancePath();
  }, [clearGovernancePath]);

  const exitClientPortal = useCallback(() => {
    setPortalTenantId(null);
    setPortalTenantName(null);
    try {
      localStorage.removeItem(PORTAL_TENANT_KEY);
      localStorage.removeItem(PORTAL_TENANT_NAME_KEY);
    } catch {
      /* ignore */
    }
    clearGovernancePath();
  }, [clearGovernancePath]);

  const persistAccess = useCallback((a: AccessSnapshot | null) => {
    setAccess(a);
    if (a) localStorage.setItem(ACCESS_KEY, JSON.stringify(a));
    else localStorage.removeItem(ACCESS_KEY);
  }, []);

  const setToken = useCallback((t: string | null, u?: AuthUser | null) => {
    setTokenState(t);
    if (u !== undefined) setUser(u);
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
      if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(ACCESS_KEY);
      setUser(null);
      setAccess(null);
    }
  }, []);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem(PORTAL_TENANT_KEY);
      localStorage.removeItem(PORTAL_TENANT_NAME_KEY);
    } catch {
      /* ignore */
    }
    setPortalTenantId(null);
    setPortalTenantName(null);
    setToken(null);
  }, [setToken]);

  const refreshAccess = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) return;
    try {
      const me = await apiService.tenantMe();
      const snap: AccessSnapshot = {
        permissions: Array.isArray(me?.permissions) ? me.permissions : [],
        enabled_modules: Array.isArray(me?.modules) ? me.modules : [],
        enabled_pages: Array.isArray(me?.enabled_pages) ? me.enabled_pages : [],
        enabled_os: Array.isArray(me?.enabled_os) ? me.enabled_os : [],
        nav_page_map: me?.nav_page_map || {},
        branding: me?.branding || null,
      };
      persistAccess(snap);
      setAccessError(false);
      if (me?.user) {
        setUser((prev) => ({
          ...(prev || me.user),
          ...me.user,
          tenant_code: me?.tenant?.code || prev?.tenant_code,
          tenant_name: me?.tenant?.name || prev?.tenant_name,
          is_platform_admin: !!(me?.is_platform_admin || me?.user?.is_platform_admin),
          is_platform_staff: !!(me?.user?.is_platform_staff || me?.is_platform_admin || me?.user?.is_platform_admin),
          platform_role: me?.user?.platform_role || prev?.platform_role,
        }));
      }
    } catch {
      // Keep the prior snapshot: it is the last answer the server gave, and
      // discarding it would fail every permission check on a transient blip.
      setAccessError(true);
    }
  }, [persistAccess]);

  const login = useCallback(async (username: string, password: string) => {
    const base = typeof window !== 'undefined' ? `${window.location.origin}/api/v1` : (process.env.REACT_APP_API_URL || '/api/v1');
    const res = await fetch(`${base}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.detail || 'Invalid username or password');
    }
    const data = await res.json();
    setToken(data.access_token, data.user);
    // Load entitlements after login
    try {
      const me = await apiService.tenantMe();
      persistAccess({
        permissions: Array.isArray(me?.permissions) ? me.permissions : [],
        enabled_modules: Array.isArray(me?.modules) ? me.modules : [],
        enabled_pages: Array.isArray(me?.enabled_pages) ? me.enabled_pages : [],
        enabled_os: Array.isArray(me?.enabled_os) ? me.enabled_os : [],
        nav_page_map: me?.nav_page_map || {},
        branding: me?.branding || null,
      });
      setAccessError(false);
    } catch {
      // An empty snapshot here would be indistinguishable from "this user has
      // no permissions", so leave it unset and let the caller retry.
      setAccessError(true);
    }
  }, [setToken, persistAccess]);

  useEffect(() => {
    const onUnauth = () => {
      setToken(null);
    };
    window.addEventListener('uem-unauthorized', onUnauth);
    return () => window.removeEventListener('uem-unauthorized', onUnauth);
  }, [setToken]);

  useEffect(() => {
    (async () => {
      if (token) {
        await refreshAccess();
      }
      setIsLoading(false);
    })();
  }, [token, refreshAccess]);

  // These three used to return true when the snapshot was empty, which meant a
  // failed or not-yet-loaded /tenant/me presented the full UI to everyone. The
  // snapshot is now the only input: absent means "not authorized yet", not
  // "authorized for everything". `access` is null only before the first
  // successful load or after logout, and the server enforces regardless.
  const can = useCallback(
    (permissionKey: string) => {
      if (isPlatformStaff) return true;
      if (!access) return false;
      return (access.permissions || []).includes(permissionKey);
    },
    [access, isPlatformStaff]
  );

  const canAny = useCallback((keys: string[]) => keys.some((k) => can(k)), [can]);

  const pageEnabled = useCallback(
    (pageKey: string) => {
      if (isPlatformStaff) return true;
      if (!access) return false;
      return (access.enabled_pages || []).includes(pageKey);
    },
    [access, isPlatformStaff]
  );

  const osEnabled = useCallback(
    (platform: string) => {
      if (isPlatformStaff) return true;
      if (!access) return false;
      const row = (access.enabled_os || []).find((o) => o.platform === platform);
      return row ? !!row.enabled : false;
    },
    [access, isPlatformStaff]
  );

  const value = useMemo(
    () => ({
      token,
      user,
      access,
      accessError,
      isAuthenticated: !!token,
      isLoading,
      isPlatformStaff,
      isPlatformAdmin,
      portalTenantId,
      portalTenantName,
      enterClientPortal,
      exitClientPortal,
      login,
      logout,
      setToken,
      refreshAccess,
      can,
      canAny,
      pageEnabled,
      osEnabled,
    }),
    [
      token,
      user,
      access,
      accessError,
      isLoading,
      isPlatformStaff,
      isPlatformAdmin,
      portalTenantId,
      portalTenantName,
      enterClientPortal,
      exitClientPortal,
      login,
      logout,
      setToken,
      refreshAccess,
      can,
      canAny,
      pageEnabled,
      osEnabled,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
